<template>
  <div class="customer-group-list-container customer-group-list-main customer-group-list-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Note Priority List']" />
    <div class="glass-tab-view glass-tab-view--flush customer-group-list-glass-shell">
      <div class="glass-tab-view__content customer-group-list-tab-content customer-group-list-page-stack">
        <div class="customer-group-list-header-block">
          <div class="customer-group-list-header-row">
            <div class="customer-group-list-header-row__text">
              <h2>Note Priorities</h2>
              <p class="customer-group-list-header-row__sub">Shared priorities for customer, vendor, and consultant notes</p>
            </div>
            <div class="customer-group-list-header-actions">
              <PrimaryButton label="Add New" @click="openAddModal" />
            </div>
          </div>
        </div>
        <div class="customer-group-list-glass-panel customer-group-list-main-card">
          <div class="consultant-glass-table-section customer-group-list-table-section">
            <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed" :class="{ 'consultant-glass-table-responsive--loading': loading }">
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table customer-group-list-table customer-group-list-table--with-actions">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th class="customer-group-list-actions-header">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="2" class="consultant-glass-table-no-records customer-group-list-no-records">Loading...</td>
                    </tr>
                    <tr v-else-if="listError">
                      <td colspan="2" class="consultant-glass-table-no-records customer-group-list-no-records">{{ listError }}</td>
                    </tr>
                    <tr v-else-if="items.length === 0">
                      <td colspan="2" class="consultant-glass-table-no-records customer-group-list-no-records">No records to show</td>
                    </tr>
                    <tr v-for="(item, idx) in items" :key="item.id || idx" class="consultant-glass-table-row customer-group-list-row">
                      <td>{{ item.name }}</td>
                      <td class="customer-group-list-actions-cell">
                        <div class="customer-group-list-action-buttons" role="group" aria-label="Note priority actions">
                          <SecondaryButton
                            label=""
                            icon="bi bi-pencil"
                            custom-class="customer-group-list-action-btn customer-group-list-action-btn--edit"
                            aria-label="Edit"
                            title="Edit"
                            @click="openEditModal(item)"
                          />
                          <SecondaryButton
                            label=""
                            icon="bi bi-trash"
                            custom-class="customer-group-list-action-btn customer-group-list-action-btn--delete"
                            aria-label="Delete"
                            title="Delete"
                            @click="openDeleteModal(idx)"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <AddNewNotePriority
      :visible="showModal"
      :company-id="companyId"
      :editing-item="editingItem"
      @ok="handleSaved"
      @cancel="closeModal"
    />
    <SuccessModal :show="showSuccessModal" title="Success" :message="successMessage" buttonText="OK" @close="showSuccessModal = false" />
    <ErrorModal :show="showErrorModal" title="Error" :message="errorMessage" buttonText="OK" @close="showErrorModal = false" />
    <WarningModal :show="showWarningModal" title="Delete Note Priority" :message="warningMessage" buttonText="Cancel" confirmButtonText="Delete" @close="closeWarningModal" @confirm="confirmDelete" />
  </div>
</template>

<script>
import '../styles/CustomerGroupList.css';
import AddNewNotePriority from './AddNewNotePriority.vue';
import noteMetaService from '@/services/noteMeta.service.js';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import WarningModal from '@/components/WarningModal.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'NotePriorityList',
  components: { AddNewNotePriority, BrucdcumHeader, SuccessModal, ErrorModal, WarningModal, PrimaryButton, SecondaryButton },
  data() {
    return {
      showModal: false,
      editingItem: null,
      items: [],
      loading: false,
      listError: '',
      companyId: this.$route.params.id,
      showSuccessModal: false,
      showErrorModal: false,
      showWarningModal: false,
      successMessage: '',
      errorMessage: '',
      warningMessage: '',
      deleteIdx: null,
    };
  },
  async mounted() {
    await this.fetchItems();
  },
  methods: {
    openAddModal() {
      this.editingItem = null;
      this.showModal = true;
    },
    openEditModal(item) {
      if (!item?.id) return;
      this.editingItem = { ...item };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingItem = null;
    },
    async fetchItems() {
      this.loading = true;
      this.listError = '';
      try {
        const response = await noteMetaService.getNotePriorities(this.companyId);
        this.items = response?.data?.data || [];
      } catch (err) {
        this.listError = err.response?.data?.error || err.response?.data?.message || 'Failed to load note priorities.';
      } finally {
        this.loading = false;
      }
    },
    async handleSaved(saved) {
      const wasEdit = !!this.editingItem?.id;
      this.closeModal();
      await this.fetchItems();
      this.successMessage = wasEdit
        ? `Note priority "${saved?.name || 'Item'}" updated successfully.`
        : `Note priority "${saved?.name || 'New priority'}" created successfully.`;
      this.showSuccessModal = true;
    },
    openDeleteModal(idx) {
      const item = this.items[idx];
      if (!item?.id) return;
      this.deleteIdx = idx;
      this.warningMessage = `Delete note priority "${item.name}"? This cannot be undone.`;
      this.showWarningModal = true;
    },
    closeWarningModal() {
      this.showWarningModal = false;
      this.deleteIdx = null;
    },
    async confirmDelete() {
      const item = this.items[this.deleteIdx];
      if (!item?.id) return;
      this.showWarningModal = false;
      try {
        await noteMetaService.deleteNotePriority(item.id, this.companyId);
        this.items.splice(this.deleteIdx, 1);
        this.successMessage = `Note priority "${item.name}" deleted successfully.`;
        this.showSuccessModal = true;
      } catch (err) {
        this.errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to delete note priority.';
        this.showErrorModal = true;
      } finally {
        this.deleteIdx = null;
      }
    },
  },
};
</script>
